import { floatToDate, formatHours } from '../shared/util/format'
import { Controller } from '@hotwired/stimulus'
import Picker from 'pickerjs'

export default class extends Controller {
  static targets = ['input', 'open']
  static values = {
    format: {
      type: String,
      default: 'HHh mm',
    },
    increment: { type: Object, default: { hour: 1, minute: 5 } },
    rows: { type: Number, default: 7 },
    date: { type: Date, default: new Date(1900, 0, 1, 0, 0) },
    breakpoint: { type: Number, default: 1000 },
  }

  connect() {
    this.dummyInput = this.findDummyInput()
    this.checkAndInitializePicker()

    this.open = this.open.bind(this)
    this.openTarget.addEventListener('click', this.open)
  }

  findDummyInput() {
    return this.inputTarget.parentElement.querySelector('input[dummy="true"]')
  }

  isMobile() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera
    const isIOS = (/iPad|iPhone|iPod/.test(userAgent) || /iPad/.test(navigator.platform)) && !window.MSStream
    const isAndroid = /Android/.test(userAgent)
    const isSmallScreen = window.matchMedia(`(max-width: ${this.breakpointValue}px)`).matches

    return isIOS || isAndroid || isSmallScreen
  }

  checkAndInitializePicker() {
    const shouldHavePicker = this.isMobile()
    const hasPicker = Boolean(this.picker)

    if (shouldHavePicker && !hasPicker) {
      this.initializePicker()
    } else if (!shouldHavePicker && hasPicker) {
      this.destroyPicker()
    }
  }

  disconnect() {
    this.destroyPicker()
    this.openTarget.removeEventListener('click', this.open)
  }

  destroyPicker() {
    if (this.picker) {
      this.picker.destroy()
      this.picker = null
      this.dummyInput = null
      this.inputTarget.removeEventListener('change', this.syncDummyInput)
    }
  }

  initializePicker() {
    if (!this.dummyInput) {
      this.dummyInput = this.findDummyInput()
    }

    if (!this.dummyInput) {
      return
    }

    this.syncDummyInput = this.syncDummyInput.bind(this)
    this.inputTarget.addEventListener('change', this.syncDummyInput)

    this.picker = new Picker(this.dummyInput, {
      format: this.formatValue,
      increment: this.incrementValue,
      controls: true,
      headers: true,
      text: this.getLocalizedText(),
      rows: this.rowsValue,
      date: this.dateValue,
    })
  }

  syncDummyInput() {
    this.dummyInput ||= this.findDummyInput()

    if (!this.dummyInput) {
      return
    }

    const pickerValue = this.inputTarget.value

    if (!pickerValue) {
      return
    }

    const decimalHours = parseFloat(pickerValue)
    const dateValue = floatToDate(decimalHours)

    this.picker.setDate(dateValue)
    this.dummyInput.value = formatHours(decimalHours)
  }

  open() {
    if (!this.isMobile()) {
      this.destroyPicker()

      return
    }

    this.checkAndInitializePicker()

    if (!this.picker) {
      return
    }

    if (this.inputTarget.value !== '') {
      const dateValue = floatToDate(parseFloat(this.inputTarget.value))

      this.picker.setDate(dateValue)
    }

    this.picker.show()
  }

  getLocalizedText() {
    const locale = typeof gon === 'undefined' ? 'fr' : gon.locale

    const translations = {
      fr: {
        title: 'Sélectionnez la durée',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        year: 'Années',
        month: 'Mois',
        day: 'Jours',
        hour: 'Heures',
        minute: 'Minutes',
        second: 'Secondes',
        millisecond: 'Millisecondes',
      },
      en: {
        title: 'Select the duration',
        cancel: 'Cancel',
        confirm: 'OK',
        year: 'Years',
        month: 'Months',
        day: 'Days',
        hour: 'Hours',
        minute: 'Minutes',
        second: 'Seconds',
        millisecond: 'Milliseconds',
      },
    }

    return translations[locale] || translations.fr
  }
}
